import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { resolveAvatarUrl } from '@/lib/avatar';
import { useBlockedUserIds } from '@/hooks/useBlockedUsers';

const ACTIVITY_PAGE_SIZE = 20;

export type ActivityType = 'review' | 'follow' | 'artist_follow' | 'wishlist_add' | 'list_create' | 'like' | 'comment' | 'mention';

export interface Activity {
  id: string;
  user_id: string;
  activity_type: ActivityType;
  metadata: {
    artist_name?: string;
    review_id?: string;
    tour_name?: string;
    following_user_id?: string;
    following_username?: string;
    following_name?: string;
    following_id?: string;
    list_name?: string;
    list_id?: string;
    target_item_name?: string;
    item_type?: string;
    item_id?: string;
    comment_id?: string;
  };
  created_at: string;
}

export interface UIActivity {
  id: string;
  type: ActivityType;
  message: string;
  timestamp: string;
  createdAt?: string;
  metadata?: any;
  user?: {
    username?: string;
    name?: string;
    avatar?: string;
  };
}


export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 60) {
    return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
  } else if (hours < 24) {
    return `${hours}h ago`;
  } else {
    return `${days}d ago`;
  }
}

export function formatActivityMessage(activity: Activity | any, userProfile?: any): string {
  // Support both 'activity_type' (interface) and 'type' (DB column)
  const activityType = activity.activity_type || activity.type;
  const metadata = activity.metadata || {};
  
  switch (activityType) {
    case 'review':
      return `${userProfile?.name || 'Someone'} reviewed ${metadata.artist_name || 'a concert'}${metadata.tour_name ? ` - ${metadata.tour_name}` : ''}`;
    case 'follow':
      return `${userProfile?.name || 'Someone'} started following ${metadata.following_name || metadata.following_username || 'someone'}`;
    case 'artist_follow':
      return `${userProfile?.name || 'Someone'} followed ${metadata.artist_name || 'an artist'}`;

    case 'wishlist_add':
      return `${userProfile?.name || 'Someone'} added ${metadata.target_item_name || metadata.artist_name || 'an item'} to their wishlist`;
    case 'list_create':
      return `${userProfile?.name || 'Someone'} created a new list: ${metadata.list_name || 'Untitled'}`;
    case 'like':
      if (metadata.item_type === 'review') {
        return `${userProfile?.name || 'Someone'} liked a review of ${metadata.artist_name || 'an artist'}`;
      } else if (metadata.item_type === 'artist') {
        return `${userProfile?.name || 'Someone'} liked ${metadata.artist_name || 'an artist'}`;
      } else if (metadata.item_type === 'tour') {
        return `${userProfile?.name || 'Someone'} liked ${metadata.tour_name || 'a tour'}`;
      } else {
        return `${userProfile?.name || 'Someone'} liked ${metadata.item_type || 'something'}`;
      }
    case 'comment':
      return `${userProfile?.name || 'Someone'} commented on a review of ${metadata.artist_name || 'an artist'}`;
    case 'mention': {
      const who = metadata.target_username ? `@${metadata.target_username}` : 'someone';
      if (metadata.source === 'list_comment') {
        return `${userProfile?.name || 'Someone'} mentioned ${who} in a list discussion`;
      }
      const subject = metadata.tour_name || metadata.artist_name;
      return subject
        ? `${userProfile?.name || 'Someone'} mentioned ${who} in a comment on ${subject}`
        : `${userProfile?.name || 'Someone'} mentioned ${who} in a comment`;
    }

    default:
      return `${userProfile?.name || 'Someone'} performed an activity`;
  }
}

function formatCurrentUserActivityMessage(activity: Activity | any): string {
  // Support both 'activity_type' (interface) and 'type' (DB column)
  const activityType = activity.activity_type || activity.type;
  const metadata = activity.metadata || {};
  
  switch (activityType) {
    case 'review':
      return `You reviewed ${metadata.artist_name || 'a concert'}${metadata.tour_name ? ` - ${metadata.tour_name}` : ''}`;
    case 'follow':
      return `You started following ${metadata.following_name || metadata.following_username || 'someone'}`;
    case 'artist_follow':
      return `You followed ${metadata.artist_name || 'an artist'}`;

    case 'wishlist_add':
      return `You added ${metadata.target_item_name || metadata.artist_name || 'an item'} to your wishlist`;
    case 'list_create':
      return `You created a new list: ${metadata.list_name || 'Untitled'}`;
    case 'like':
      if (metadata.item_type === 'review') {
        return `You liked a review of ${metadata.artist_name || 'an artist'}`;
      } else if (metadata.item_type === 'artist') {
        return `You liked the artist ${metadata.artist_name || 'an artist'}`;
      } else if (metadata.item_type === 'tour') {
        return `You liked the ${metadata.tour_name || 'a tour'} tour`;
      } else if (metadata.item_type === 'list') {
        return `You liked ${metadata.list_name || 'list'}`;
      } else {
        return `You liked ${metadata.item_type || 'something'}`;
      }
    case 'comment':
      return `You commented on a review of ${metadata.artist_name || 'an artist'}`;
    case 'mention': {
      const who = metadata.target_username ? `@${metadata.target_username}` : 'someone';
      if (metadata.source === 'list_comment') return `You mentioned ${who} in a list discussion`;
      const subject = metadata.tour_name || metadata.artist_name;
      return subject ? `You mentioned ${who} in a comment on ${subject}` : `You mentioned ${who} in a comment`;
    }

    default:
      return `You performed an activity`;
  }
}

export const useFriendsActivities = () => {
  const { user } = useAuth();
  const { blockedIds } = useBlockedUserIds();
  const blockedKey = Array.from(blockedIds).sort();

  const query = useInfiniteQuery({
    queryKey: ['friends-activities', user?.id, blockedKey.join(',')],
    enabled: !!user,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      if (!user) return { items: [] as UIActivity[], nextCursor: null as string | null };

      // Resolve followed users once (per page is fine; tiny query)
      const { data: following, error: followingError } = await supabase
        .from('followers')
        .select('following_id')
        .eq('follower_id', user.id);

      if (followingError) {
        console.error('Error fetching following users:', followingError);
        return { items: [] as UIActivity[], nextCursor: null };
      }
      if (!following?.length) {
        return { items: [] as UIActivity[], nextCursor: null };
      }

      const followingIds = following
        .map((f) => f.following_id)
        .filter((id) => !blockedKey.includes(id));
      if (followingIds.length === 0) {
        return { items: [] as UIActivity[], nextCursor: null };
      }

      let q = (supabase as any)
        .from('activities')
        .select('*')
        .in('user_id', followingIds)
        .order('created_at', { ascending: false })
        .limit(ACTIVITY_PAGE_SIZE);
      if (pageParam) q = q.lt('created_at', pageParam as string);

      const { data: activities, error } = await q;
      if (error) {
        console.error('Error fetching friends activities:', error);
        return { items: [] as UIActivity[], nextCursor: null };
      }

      const rawList = (activities || []) as any[];
      console.log('[NotificationsPagination] fetch', {
        tab: 'following',
        pageParam,
        requested: ACTIVITY_PAGE_SIZE,
        returned: rawList.length,
      });

      const activitiesWithProfiles = await Promise.all(
        rawList.map(async (activity: any) => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('username, name, profile_picture')
          .eq('id', activity.user_id)
          .maybeSingle();

        let enrichedMeta = { ...(activity.metadata || {}) } as any;
        let message = formatActivityMessage(activity, profile);

        // Enrich metadata for proper hyperlinking
        // Note: DB column is 'type', not 'activity_type'
        const activityType = activity.type;
        try {
          if (activityType === 'follow' && activity.metadata?.following_id) {
            const { data: followedProfile } = await supabase
              .from('profiles')
              .select('name, username')
              .eq('id', activity.metadata.following_id)
              .maybeSingle();
            enrichedMeta = { ...enrichedMeta, following_name: followedProfile?.name || followedProfile?.username, following_username: followedProfile?.username };
            message = `${profile?.name || 'Someone'} started following ${followedProfile?.name || followedProfile?.username || 'someone'}`;
          } else if (activityType === 'wishlist_add') {
            const itemType = activity.metadata?.item_type;
            const itemId = activity.metadata?.item_id;
            if (itemType === 'artist' && itemId) {
              const { data: artistRow } = await supabase
                .from('artists')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, target_item_name: artistRow?.name, artist_id: itemId };
              message = `${profile?.name || 'Someone'} added ${artistRow?.name || 'an item'} to their wishlist`;
            } else if (itemType === 'tour' && itemId) {
              const { data: tourRow } = await supabase
                .from('tours')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, target_item_name: tourRow?.name, tour_id: itemId };
              message = `${profile?.name || 'Someone'} added ${tourRow?.name || 'an item'} to their wishlist`;
            }
          } else if (activityType === 'comment' && activity.metadata?.review_id) {
            const { data: reviewRow } = await supabase
              .from('reviews')
              .select('id, user_id, artist_name, tour_name')
              .eq('id', activity.metadata.review_id)
              .maybeSingle();
            enrichedMeta = { ...enrichedMeta, review_id: activity.metadata.review_id, artist_name: reviewRow?.artist_name, tour_name: reviewRow?.tour_name };
            const tourPart = reviewRow?.tour_name ? ` (${reviewRow.tour_name})` : '';
            message = `${profile?.name || 'Someone'} commented on a review of ${reviewRow?.artist_name || 'an artist'}${tourPart}`;
          } else if (activityType === 'like') {
            const itemType = activity.metadata?.item_type;
            const itemId = activity.metadata?.item_id || activity.metadata?.review_id;
            if (itemType === 'artist' && itemId) {
              const { data: artistRow } = await supabase
                .from('artists')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, artist_name: artistRow?.name, item_id: itemId };
              message = `${profile?.name || 'Someone'} liked ${artistRow?.name || 'an artist'}`;
            } else if (itemType === 'review' && itemId) {
              const { data: reviewRow2 } = await supabase
                .from('reviews')
                .select('user_id, artist_name, tour_name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, review_id: itemId, artist_name: reviewRow2?.artist_name, tour_name: reviewRow2?.tour_name };
              const tourPart = reviewRow2?.tour_name ? ` (${reviewRow2.tour_name})` : '';
              message = `${profile?.name || 'Someone'} liked a review of ${reviewRow2?.artist_name || 'an artist'}${tourPart}`;
            } else if (itemType === 'tour' && itemId) {
              const { data: tourRow } = await supabase
                .from('tours')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, tour_name: tourRow?.name, item_id: itemId };
              message = `${profile?.name || 'Someone'} liked ${tourRow?.name || 'a tour'}`;
            }
          } else if (activityType === 'artist_follow') {
            const artistId = activity.metadata?.artist_id || activity.target_id;
            let artistName = activity.metadata?.artist_name;
            if (!artistName && artistId) {
              const { data: artistRow } = await supabase
                .from('artists').select('name').eq('id', artistId).maybeSingle();
              artistName = artistRow?.name;
            }
            enrichedMeta = { ...enrichedMeta, item_type: 'artist', artist_id: artistId, artist_name: artistName };
            message = `${profile?.name || 'Someone'} followed ${artistName || 'an artist'}`;
          } else if (activityType === 'review') {

            const artistName = activity.metadata?.artist_name;
            const reviewId = activity.metadata?.review_id;
            enrichedMeta = { ...enrichedMeta, artist_name: artistName, review_id: reviewId };
            message = `${profile?.name || 'Someone'} reviewed ${artistName || 'a concert'}${activity.metadata?.tour_name ? ` - ${activity.metadata.tour_name}` : ''}`;
          } else if (activityType === 'mention' && activity.metadata?.source === 'list_comment' && activity.metadata?.list_id) {
            const { data: listRow } = await supabase
              .from('user_lists').select('name').eq('id', activity.metadata.list_id).maybeSingle();
            enrichedMeta = { ...enrichedMeta, list_name: listRow?.name };
            const who = enrichedMeta.target_username ? `@${enrichedMeta.target_username}` : 'someone';
            message = `${profile?.name || 'Someone'} mentioned ${who} in ${listRow?.name ? `"${listRow.name}"` : 'a list discussion'}`;
          }
        } catch (e) {
          console.warn('Activity enrichment failed:', e);
        }


        return {
          id: activity.id,
          type: activityType,
          message,
          timestamp: formatTimestamp(activity.created_at),
          createdAt: activity.created_at,
          metadata: enrichedMeta,
          user: {
            username: profile?.username || 'unknown',
            name: profile?.name || 'Unknown User',
            avatar: resolveAvatarUrl(profile, null, 'friends-activity')
          }
        } as UIActivity;
      }));

      const nextCursor =
        rawList.length === ACTIVITY_PAGE_SIZE
          ? (rawList[rawList.length - 1]?.created_at as string) || null
          : null;
      console.log('[NotificationsPagination] page result', {
        tab: 'following',
        rawCount: rawList.length,
        nextCursor,
      });

      return { items: activitiesWithProfiles, nextCursor };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const data = useMemo(() => {
    const seen = new Set<string>();
    const out: UIActivity[] = [];
    let dupes = 0;
    (query.data?.pages || []).forEach((p) =>
      p.items.forEach((a) => {
        if (seen.has(a.id)) { dupes++; return; }
        seen.add(a.id);
        out.push(a);
      })
    );
    if (dupes > 0) console.log('[NotificationsPagination] deduped', { tab: 'following', skipped: dupes });
    console.log('[NotificationsPagination] total loaded', { tab: 'following', total: out.length });
    return out;
  }, [query.data]);

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
  };
};

export function useCurrentUserActivities() {
  const { user } = useAuth();

  const query = useInfiniteQuery({
    queryKey: ['current-user-activities', user?.id],
    enabled: !!user,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      if (!user) return { items: [] as UIActivity[], nextCursor: null as string | null };

      let q = (supabase as any)
        .from('activities')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(ACTIVITY_PAGE_SIZE);
      if (pageParam) q = q.lt('created_at', pageParam as string);

      const { data, error } = await q;
      if (error) {
        console.error('Error fetching user activities:', error);
        return { items: [] as UIActivity[], nextCursor: null };
      }

      const rawList = (data || []) as any[];
      console.log('[NotificationsPagination] fetch', {
        tab: 'you',
        pageParam,
        requested: ACTIVITY_PAGE_SIZE,
        returned: rawList.length,
      });

      const uiActivities: UIActivity[] = await Promise.all(rawList.map(async (activity: any) => {
        let message = formatCurrentUserActivityMessage(activity);
        let enrichedMeta = { ...(activity.metadata || {}) } as any;
        // Note: DB column is 'type', not 'activity_type'
        const activityType = activity.type;
        try {
          if (activityType === 'follow' && activity.metadata?.following_id) {
            const { data: followedProfile } = await supabase
              .from('profiles')
              .select('name, username')
              .eq('id', activity.metadata.following_id)
              .maybeSingle();
            const followedName = followedProfile?.name || followedProfile?.username;
            enrichedMeta = { ...enrichedMeta, following_name: followedName, following_username: followedProfile?.username };
            message = `You started following ${followedName || 'someone'}`;
          } else if (activityType === 'wishlist_add') {
            const itemType = activity.metadata?.item_type;
            const itemId = activity.metadata?.item_id;
            if (itemType === 'artist' && itemId) {
              const { data: artistRow } = await supabase
                .from('artists')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, target_item_name: artistRow?.name, artist_id: itemId };
              message = `You added ${artistRow?.name || 'an artist'} to your wishlist`;
            } else if (itemType === 'tour' && itemId) {
              const { data: tourRow } = await supabase
                .from('tours')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, target_item_name: tourRow?.name, tour_id: itemId };
              message = `You added ${tourRow?.name || 'a tour'} to your wishlist`;
            }
          } else if (activityType === 'comment' && activity.metadata?.review_id) {
            const { data: reviewRow } = await supabase
              .from('reviews')
              .select('id, user_id, artist_name, tour_name')
              .eq('id', activity.metadata.review_id)
              .maybeSingle();
            let authorName: string | undefined;
            if (reviewRow?.user_id) {
              const { data: reviewAuthor } = await supabase
                .from('profiles')
                .select('name, username')
                .eq('id', reviewRow.user_id)
                .maybeSingle();
              authorName = reviewAuthor?.name || reviewAuthor?.username;
            }
            enrichedMeta = { ...enrichedMeta, review_id: activity.metadata.review_id, artist_name: reviewRow?.artist_name, author_name: authorName, tour_name: reviewRow?.tour_name };
            const tourPart = reviewRow?.tour_name ? ` (${reviewRow.tour_name})` : '';
            message = `You commented on ${authorName ? `${authorName}'s` : 'a'} review of ${reviewRow?.artist_name || 'an artist'}${tourPart}`;
          } else if (activityType === 'like') {
            const itemType = activity.metadata?.item_type;
            const itemId = activity.metadata?.item_id || activity.metadata?.review_id;
            if (itemType === 'artist' && itemId) {
              const { data: artistRow } = await supabase
                .from('artists')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, artist_name: artistRow?.name, item_id: itemId };
              message = `You liked the artist ${artistRow?.name || 'Unknown Artist'}`;
            } else if (itemType === 'review' && itemId) {
              const { data: reviewRow2 } = await supabase
                .from('reviews')
                .select('user_id, artist_name, tour_name')
                .eq('id', itemId)
                .maybeSingle();
              let authorName2: string | undefined;
              if (reviewRow2?.user_id) {
                const { data: reviewAuthor2 } = await supabase
                  .from('profiles')
                  .select('name, username')
                  .eq('id', reviewRow2.user_id)
                  .maybeSingle();
                authorName2 = reviewAuthor2?.name || reviewAuthor2?.username;
              }
              enrichedMeta = { ...enrichedMeta, review_id: itemId, artist_name: reviewRow2?.artist_name, tour_name: reviewRow2?.tour_name };
              const tourPart = reviewRow2?.tour_name ? ` (${reviewRow2.tour_name})` : '';
              message = `You liked ${authorName2 ? `${authorName2}'s` : 'a'} review of ${reviewRow2?.artist_name || 'an artist'}${tourPart}`;
            } else if (itemType === 'tour' && itemId) {
              const { data: tourRow } = await supabase
                .from('tours')
                .select('name')
                .eq('id', itemId)
                .maybeSingle();
              enrichedMeta = { ...enrichedMeta, tour_name: tourRow?.name, item_id: itemId };
              message = `You liked the ${tourRow?.name ? `${tourRow.name} tour` : 'tour'}`;
            }
          } else if (activityType === 'artist_follow') {
            const artistId = activity.metadata?.artist_id || activity.target_id;
            let artistName = activity.metadata?.artist_name;
            if (!artistName && artistId) {
              const { data: artistRow } = await supabase
                .from('artists').select('name').eq('id', artistId).maybeSingle();
              artistName = artistRow?.name;
            }
            enrichedMeta = { ...enrichedMeta, item_type: 'artist', artist_id: artistId, artist_name: artistName };
            message = `You followed ${artistName || 'an artist'}`;
          } else if (activityType === 'review') {

            enrichedMeta = { ...enrichedMeta, artist_name: activity.metadata?.artist_name, review_id: activity.target_id };
            const tourPart = activity.metadata?.tour_name ? ` - ${activity.metadata.tour_name}` : '';
            message = `You reviewed ${activity.metadata?.artist_name || 'a concert'}${tourPart}`;
          } else if (activityType === 'mention' && activity.metadata?.source === 'list_comment' && activity.metadata?.list_id) {
            const { data: listRow } = await supabase
              .from('user_lists').select('name').eq('id', activity.metadata.list_id).maybeSingle();
            enrichedMeta = { ...enrichedMeta, list_name: listRow?.name };
            const who = enrichedMeta.target_username ? `@${enrichedMeta.target_username}` : 'someone';
            message = `You mentioned ${who} in ${listRow?.name ? `"${listRow.name}"` : 'a list discussion'}`;
          }

        } catch (e) {
          console.warn('Current user activity enrichment failed:', e);
        }

        return {
          id: activity.id,
          type: activityType,
          message,
          timestamp: formatTimestamp(activity.created_at),
          createdAt: activity.created_at,
          metadata: enrichedMeta,
        } as UIActivity;
      }));

      const nextCursor =
        rawList.length === ACTIVITY_PAGE_SIZE
          ? (rawList[rawList.length - 1]?.created_at as string) || null
          : null;
      console.log('[NotificationsPagination] page result', {
        tab: 'you',
        rawCount: rawList.length,
        nextCursor,
      });

      return { items: uiActivities, nextCursor };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const data = useMemo(() => {
    const seen = new Set<string>();
    const out: UIActivity[] = [];
    let dupes = 0;
    (query.data?.pages || []).forEach((p) =>
      p.items.forEach((a) => {
        if (seen.has(a.id)) { dupes++; return; }
        seen.add(a.id);
        out.push(a);
      })
    );
    if (dupes > 0) console.log('[NotificationsPagination] deduped', { tab: 'you', skipped: dupes });
    console.log('[NotificationsPagination] total loaded', { tab: 'you', total: out.length });
    return out;
  }, [query.data]);

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
  };
}

export function useTrackActivity() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      type, 
      metadata 
    }: { 
      type: ActivityType; 
      metadata: Activity['metadata'] 
    }) => {
      if (!user) throw new Error('User not authenticated');

      // Use type assertion for now until activities table is in types
      const { data, error } = await (supabase as any)
        .from('activities')
        .insert({
          user_id: user.id,
          type: type,
          target_type: metadata.item_type || (type === 'review' ? 'review' : type === 'follow' ? 'user' : type === 'list_create' ? 'list' : null),
          target_id: metadata.review_id || metadata.item_id || metadata.following_id || metadata.list_id || null,
          metadata
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['friends-activities'] });
      queryClient.invalidateQueries({ queryKey: ['current-user-activities'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}